// Goal-type icons arrive from QP as raw SVG markup (tenant configuration). Never
// inject them as-is: parse, keep only plain drawing elements and attributes, and
// recolour the stroke to currentColor so the glyph follows Andersen's tokens.

const ALLOWED_TAGS = new Set(["svg", "g", "path", "circle", "ellipse", "rect", "line", "polyline", "polygon"]);
const ALLOWED_ATTRS = new Set([
  "viewbox",
  "d",
  "cx",
  "cy",
  "r",
  "rx",
  "ry",
  "x",
  "y",
  "x1",
  "x2",
  "y1",
  "y2",
  "width",
  "height",
  "points",
  "fill",
  "stroke",
  "stroke-width",
  "stroke-linecap",
  "stroke-linejoin",
  "transform",
]);

const cache = new Map<string, string | null>();

export function sanitizeIconSvg(raw: string | null | undefined): string | null {
  if (!raw || typeof DOMParser === "undefined") return null;
  const hit = cache.get(raw);
  if (hit !== undefined) return hit;

  let out: string | null = null;
  try {
    const doc = new DOMParser().parseFromString(raw, "image/svg+xml");
    const root = doc.documentElement;
    if (root && root.nodeName.toLowerCase() === "svg" && !doc.querySelector("parsererror")) {
      const clean = (el: Element): string => {
        const tag = el.nodeName.toLowerCase();
        if (!ALLOWED_TAGS.has(tag)) return "";
        const attrs: string[] = [];
        for (const a of Array.from(el.attributes)) {
          const name = a.name.toLowerCase();
          if (!ALLOWED_ATTRS.has(name)) continue;
          let value = a.value;
          if (/url\(|javascript:|expression/i.test(value)) continue;
          if (name === "stroke" && value !== "none") value = "currentColor";
          if (name === "fill" && value !== "none") value = "currentColor";
          attrs.push(`${name === "viewbox" ? "viewBox" : name}="${value.replace(/"/g, "&quot;")}"`);
        }
        if (tag === "svg") attrs.push('aria-hidden="true"', 'focusable="false"');
        const kids = Array.from(el.children).map(clean).join("");
        return `<${tag} ${attrs.join(" ")}>${kids}</${tag}>`;
      };
      out = clean(root);
    }
  } catch {
    out = null;
  }
  cache.set(raw, out);
  return out;
}
